import React from "react";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
} from "@chakra-ui/react";
import { Controller, useFormContext } from "react-hook-form";
import {
  AutoComplete,
  AutoCompleteInput,
  AutoCompleteItem,
  AutoCompleteList,
} from "@choc-ui/chakra-autocomplete";

import { IFormField } from "./fieldTypes";
import { JsonFormSelectOption } from "../types";

/**
 * JsonForm Select Field component (Upgraded to Searchable Autocomplete).
 * @param props see {@link IFormField}
 */
function SelectField({
  field,
  isRequired,
  disabled = false,
  errorMessage = "",
}: IFormField) {
  const { control } = useFormContext();

  return (
    <FormControl isRequired={isRequired} isInvalid={!!errorMessage}>
      <FormLabel>{field.label}</FormLabel>

      <Controller
        control={control}
        name={field.fieldName}
        rules={field.config}
        render={({ field: { onChange, value } }) => (
          <AutoComplete
            openOnFocus
            suggestWhenEmpty
            onChange={onChange} // Only updates the form when a valid option is clicked/entered
            value={value} // Keeps the dropdown synced with the form's actual data
          >
            <AutoCompleteInput
              disabled={disabled}
              placeholder="~~ Select One ~~"
              variant="outline"
            />
            <AutoCompleteList>
              {[...(field.options || [])]
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((selectOption: JsonFormSelectOption) => (
                  <AutoCompleteItem
                    key={selectOption.value}
                    value={selectOption.value}
                    label={selectOption.label}
                    textTransform="none"
                  >
                    {selectOption.label}
                  </AutoCompleteItem>
                ))}
            </AutoCompleteList>
          </AutoComplete>
        )}
      />

      <FormErrorMessage>{errorMessage}</FormErrorMessage>
      {!errorMessage && field.helperText && (
        <FormHelperText>{field.helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

export { SelectField };
